import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash-es';
import Modal from 'sushiweb/Modal';
import Button from 'sushiweb/Button/Button';
import H6 from 'sushiweb/Typography/Heading/H6';
import Download from 'sushiweb/Icons/all/Download';
import CheckCircleFill from 'sushiweb/Icons/all/CheckCircleFill';
import Cross from 'sushiweb/Icons/all/Cross';
import green from 'sushiweb/tokens/color/green';
import grey from 'sushiweb/tokens/color/grey';
import { checkFileTypeSize, clearInputValue } from '../../../helpers';
import { wrongFormat } from '../../common/components/constants';
import zfetch, { METHOD_POST } from '../../common/utils/zfetch';
import { UPLOAD_OPTIONS_FILE_URL } from '../../../constants/urls';
import {
  UploadZone,
  HiddenInput,
  ZoneText,
  InstructionText,
  FileRow,
  FileName,
  SuccessContainer,
  ButtonWrapper,
} from './styles';

const downloadSampleCSV = () => {
  const csvContent = 'Options\nOption1\nOption2\nOption3';
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'options_sample.csv';
  a.click();
  URL.revokeObjectURL(url);
};

function UploadOptionsModal({ isOpen, onClose, data, companyId, showErrorToast, showSuccessToast, onUploadSuccess }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleClose = () => {
    setFile(null);
    setUploadSuccess(false);
    onClose();
  };

  const onChange = e => {
    checkFileTypeSize(e.target.files[0])
      .then(success => {
        if (success) {
          setFile(e.target.files[0]);
        } else {
          showErrorToast(wrongFormat);
          clearInputValue();
        }
      })
      .catch(() => {
        clearInputValue();
        showErrorToast(wrongFormat);
      });
  };

  const handleClearFile = e => {
    e.preventDefault();
    setFile(null);
  };

  const uploadOptions = () => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('company_id', companyId);
    formData.append('generate_attachment', false);
    formData.append('upload_usecase', 4);
    formData.append('file_path', `options/${companyId}`);
    formData.append('question_id', data?.question_id);

    setLoading(true);
    zfetch(UPLOAD_OPTIONS_FILE_URL, { method: METHOD_POST, body: formData })
      .then(res => res.json())
      .then(res => {
        setLoading(false);
        if (!res.status) {
          showErrorToast(res.message || 'Upload failed. Please try again.');
          return;
        }
        setUploadSuccess(true);
        showSuccessToast('Options overwritten successfully!');
        onUploadSuccess();
      })
      .catch(() => {
        setLoading(false);
        showErrorToast('Upload failed. Please try again.');
      });
  };

  return (
    <Modal
      title='Upload Options'
      onClose={handleClose}
      visible={isOpen}
      type='medium'
      isCentered
      padding='2rem'
    >
      {uploadSuccess ? (
        <SuccessContainer>
          <CheckCircleFill color={green.z500} size='6rem' />
          <H6>Options uploaded successfully!</H6>
          <Button appearance='primary' size='large' onClick={handleClose} style={{ width: '100%', justifyContent: 'center' }}>
            Okay
          </Button>
        </SuccessContainer>
      ) : (
        <>
          <UploadZone htmlFor='upload-options-file' disabled={!!file}>
            <HiddenInput
              id='upload-options-file'
              type='file'
              accept='.csv'
              onChange={onChange}
              disabled={!!file}
            />
            <ZoneText disabled={!!file}>Upload options CSV</ZoneText>
          </UploadZone>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '6px 0' }}>
            <div>
              <InstructionText>Format: .csv files</InstructionText>
              <InstructionText>File size: upto 5 MB</InstructionText>
            </div>
            <Button
              appearance='outline'
              icon={<Download />}
              iconAlign='left'
              btnColor='coal'
              onClick={downloadSampleCSV}
              size='small'
              style={{ background: grey.z100, color: '#293142', border: '0px', flexShrink: 0 }}
            >
              Download sample file
            </Button>
          </div>

          {file && (
            <FileRow>
              <FileName>{file.name}</FileName>
              <Button
                appearance='outline'
                icon={<Cross />}
                onClick={handleClearFile}
                size='small'
                style={{ background: 'transparent', color: grey.z600, border: '0px', padding: '0px', flexShrink: 0 }}
              />
            </FileRow>
          )}

          <ButtonWrapper>
            <Button
              appearance='primary'
              size='large'
              disabled={!file}
              onClick={uploadOptions}
              loading={loading}
              style={{ width: '100%', justifyContent: 'center' }}
            >
              Upload
            </Button>
          </ButtonWrapper>
        </>
      )}
    </Modal>
  );
}

UploadOptionsModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  data: PropTypes.objectOf(PropTypes.any),
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showErrorToast: PropTypes.func,
  showSuccessToast: PropTypes.func,
  onUploadSuccess: PropTypes.func,
};

UploadOptionsModal.defaultProps = {
  isOpen: false,
  onClose: noop,
  data: {},
  companyId: '',
  showErrorToast: noop,
  showSuccessToast: noop,
  onUploadSuccess: noop,
};

export default UploadOptionsModal;
